import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { Toast, copyToClipboard, useToast } from "@/components/base/Toast";
import { usePageMeta } from "@/hooks/usePageMeta";

const EMAIL = "content@daqiaoweijiu-demo.com";

const services = [
  {
    icon: "ri-film-line",
    title: "作品档案整理",
    description:
      "按电影与电视剧分类整理作品条目，包含上映年份、类型、片长或集数、剧情简介与演职员信息。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选检索",
    description:
      "支持按年份区间、作品状态与排序方式筛选，也可以直接搜索作品标题、类型标签或资讯关键词。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯动态汇总",
    description:
      "收录作品发布、行业观察、幕后记录与影评观点，按时间与热度排列，方便快速了解近期动态。",
  },
  {
    icon: "ri-bookmark-line",
    title: "本地收藏",
    description:
      "作品收藏保存在浏览器本地，无需注册账号，也不会向服务器上传任何个人数据。",
  },
];

const facts = [
  { label: "站点类型", value: "影视内容聚合 / 演示站点" },
  { label: "内容范围", value: "电影、电视剧、行业资讯" },
  { label: "作品条目", value: "20 部（含 1 部待播）" },
  { label: "资讯条目", value: "9 篇" },
  { label: "数据来源", value: "站内演示数据" },
  { label: "更新节奏", value: "每周一、周四" },
];

export default function AboutPage() {
  const { message, show } = useToast();

  usePageMeta(
    "关于本站 - 大桥未久影视作品档案",
    "了解大桥未久影视内容站的定位、内容范围、更新方式，以及版权说明、免责声明与联系反馈方式。",
    "关于本站,大桥未久影视网站,网站介绍,免责声明,联系方式",
  );

  const handleCopyEmail = async () => {
    const success = await copyToClipboard(EMAIL);
    show(success ? "邮箱地址已复制" : "复制失败，请手动选择邮箱地址");
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />
          <div className="mt-6 max-w-3xl">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-700">
              About this site
            </p>
            <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
              关于本站
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              一个专注影视作品资料整理的内容站，把零散的作品信息、演职员资料与行业动态，
              整理成可检索、可收藏的清晰条目。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            <section>
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                网站定位
              </h2>
              <div className="mt-4 space-y-3.5 text-sm leading-[1.9] text-foreground-700">
                <p>
                  「大桥未久影视内容站」围绕演员大桥未久的影视作品展开，聚合电影与电视剧的
                  作品资料、演员信息与相关行业资讯，面向影视观众、作品爱好者与内容研究者。
                </p>
                <p>
                  站点不做播放与下载，也不提供任何付费内容。我们更关心的是：一部作品的
                  上映信息、主创名单与故事梗概，能否被快速、准确地找到。
                </p>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {services.map((service) => (
                  <div
                    key={service.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${service.icon} text-lg`}></i>
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-950">
                      {service.title}
                    </h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                      {service.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10 border-t border-background-200 pt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                内容与数据说明
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-[1.9] text-foreground-700">
                <p>
                  本站目前为前端演示版本，页面中的作品名称、剧情简介、演职员名单、评分与资讯内容
                  均为虚构示例，用于展示信息结构与交互流程，不代表任何真实作品或人物信息。
                </p>
                <p>
                  搜索、筛选、排序与分页均在浏览器端即时完成，不请求后台接口；收藏记录保存在
                  浏览器本地存储中，清除浏览器数据后会被重置。
                </p>
              </div>
              <div className="mt-4 flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
                <i className="ri-alert-line mt-0.5 text-accent-700"></i>
                <p className="text-xs leading-relaxed text-accent-900">
                  本站不提供视频播放、资源下载、在线购买或任何形式的付费服务，请勿将演示内容
                  作为真实资料引用。
                </p>
              </div>
            </section>

            <section className="mt-10 border-t border-background-200 pt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                版权说明与免责声明
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-[1.9] text-foreground-700">
                <li className="flex gap-2.5">
                  <span className="font-label mt-0.5 text-xs font-semibold text-accent-600">01</span>
                  <span>
                    站内文字内容由本站整理撰写，仅供个人学习、研究与浏览使用，未经许可不得
                    用于商业用途或批量转载。
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <span className="font-label mt-0.5 text-xs font-semibold text-accent-600">02</span>
                  <span>
                    页面配图均为生成的示意图片，仅用于版式演示，与任何真实作品、海报或人物无关。
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <span className="font-label mt-0.5 text-xs font-semibold text-accent-600">03</span>
                  <span>
                    如相关内容涉及权利人权益，请通过下方邮箱与我们联系，我们会在核实后及时
                    调整或删除。
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <span className="font-label mt-0.5 text-xs font-semibold text-accent-600">04</span>
                  <span>
                    本站不对演示数据的准确性、完整性作出承诺，因使用本站内容产生的任何后果，
                    本站不承担责任。
                  </span>
                </li>
              </ul>
            </section>

            <section className="mt-10 border-t border-background-200 pt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                联系方式与反馈
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                资料纠错、内容建议与版权事宜，欢迎通过邮箱联系。我们通常在三个工作日内回复。
              </p>

              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] text-foreground-400">联系邮箱</p>
                    <a
                      href={`mailto:${EMAIL}`}
                      className="block truncate text-sm text-foreground-900 underline-offset-4 transition-colors hover:text-accent-700 hover:underline"
                    >
                      {EMAIL}
                    </a>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="inline-flex min-h-[44px] shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                >
                  <i className="ri-file-copy-line"></i>
                  复制邮箱
                </button>
              </div>
            </section>
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">站点信息</h2>
              <dl className="mt-4 space-y-3 text-xs">
                {facts.map((fact) => (
                  <div key={fact.label} className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">{fact.label}</dt>
                    <dd className="text-right text-foreground-900">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6 rounded-lg bg-primary-900 p-5 text-background-200">
              <h2 className="text-sm font-semibold text-background-50">快捷入口</h2>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to="/"
                  className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-background-50/20 px-3.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-home-5-line"></i>
                    返回首页
                  </span>
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link
                  to="/category/movie"
                  className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-background-50/20 px-3.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-film-line"></i>
                    电影作品
                  </span>
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link
                  to="/category/tv"
                  className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-background-50/20 px-3.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-tv-2-line"></i>
                    电视剧作品
                  </span>
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link
                  to="/news"
                  className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-background-50/20 px-3.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-newspaper-line"></i>
                    资讯中心
                  </span>
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            </div>

            <p className="mt-6 text-[11px] leading-relaxed text-foreground-400">
              本站为个人整理的影视资料站，与任何影视公司、发行方或播出平台无隶属关系。
            </p>
          </aside>
        </div>
      </div>

      <Toast message={message} />
    </SiteLayout>
  );
}